import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { ITemplate, ITemplateLanguageStatusEnum } from 'entities/template/template';
import { TemplateTypes } from '../../../constants/templateTypes';
import {
  DEFAULT_EMAIL_TRACKING_UNSUBSCRIBE_PAGE_TEMPLATE,
  DEFAULT_REMARKETING_EMAIL_TEMPLATE,
  DEFAULT_UNSUBSCRIBE_PAGE_TEMPLATE,
} from 'sections/design/template-management/constants/default-templates';
import { getDefaultRCSTemplate } from 'sections/design/template-management/constants';
import { generatePath, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useTemplatesListDispatch } from '../../../context/UseTemplatesListContext';
import useNotifications from 'core/notifications/useNotifications';
import { addOffsiteTemplateLanguage, addOnsiteTemplateLanguage } from '../../../utils/queries';
import { getEditRoute } from '../utils/getEditRoute';

const getMutationByTypeAndChannel = (type: keyof typeof TemplateTypes, channel: string) => {
  switch (type) {
    case TemplateTypes.dataAcquisition:
    case TemplateTypes.sendMyBasket:
    case TemplateTypes.mpsEmbed:
    case TemplateTypes.mpsPopup:
    case TemplateTypes.displayOnly:
      if (channel === 'email') {
        return addOffsiteTemplateLanguage;
      }
      return addOnsiteTemplateLanguage;
    default:
      return addOffsiteTemplateLanguage;
  }
};

const templateHtmlByType = {
  [TemplateTypes.unsubscribe]: DEFAULT_UNSUBSCRIBE_PAGE_TEMPLATE,
  [TemplateTypes.unsubscribeEmailTracking]: DEFAULT_EMAIL_TRACKING_UNSUBSCRIBE_PAGE_TEMPLATE,
};

export const useAddLanguage = (selectedTemplate: ITemplate) => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages', 'templates']);
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useTemplatesListDispatch();
  const { showNotification } = useNotifications();

  const { type, id, channel } = selectedTemplate;

  const addLanguageMutation = useMutation(getMutationByTypeAndChannel(type as keyof typeof TemplateTypes, channel));
  const templateHtml =
    channel === 'rcs'
      ? getDefaultRCSTemplate()
      : templateHtmlByType[type as keyof typeof templateHtmlByType] ?? DEFAULT_REMARKETING_EMAIL_TEMPLATE;

  return useCallback(
    (language: string) => {
      addLanguageMutation.mutate(
        {
          siteId,
          templateId: id,
          language,
          template: templateHtml,
          comment: t('designs:designGallery.templateList.queries.addTemplateLanguage.comment'),
          type,
          t,
        },
        {
          onSuccess: () => {
            window.open(generatePath(getEditRoute(selectedTemplate), { siteId, id, language }) + '?newtab=1', '_blank');
            dispatch({
              type: 'SET_SELECTED_TEMPLATE',
              payload: {
                template: {
                  ...selectedTemplate,
                  supportedLanguages: [
                    ...selectedTemplate.supportedLanguages,
                    { language, status: ITemplateLanguageStatusEnum.Draft },
                  ],
                },
              },
            });
          },
          onError: (error: Error) => {
            showNotification(error.message, 'error');
          },
        },
      );
    },
    [addLanguageMutation, dispatch, id, selectedTemplate, showNotification, siteId, t, templateHtml, type],
  );
};
